import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import EmergencyButton from '../components/EmergencyButton';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';

function AdminInboxPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
                body: JSON.stringify({
          content: newMessage
        })
      });

      if (response.ok) {
        toast.success('Message sent');
        setNewMessage('');
        fetchMessages();
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('An error occurred');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/${messageId}/read`, {
        method: 'PATCH',
              });
      fetchMessages();
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <PageHeader title="Admin Inbox" />

      <main className="pt-20 pb-24 px-4">
        <div className="max-w-md mx-auto">
          {/* Messages */}
          <div className="space-y-3 mb-6">
            {messages.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-md p-6 text-center">
                <p className="text-sm text-zinc-500">No messages yet</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.message_id}
                  className={`bg-white border rounded-md p-4 ${
                    message.from_admin ? 'border-blue-200 bg-blue-50' : 'border-zinc-200'
                  } ${!message.read && !message.from_admin ? 'border-l-4 border-l-zinc-950' : ''}`}
                  onClick={() => !message.read && !message.from_admin && markAsRead(message.message_id)}
                  data-testid={`message-${message.message_id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
                      {message.from_admin ? 'Admin' : 'You'}
                    </span>
                    <span className="text-xs text-zinc-400">
                      {new Date(message.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-700">{message.content}</p>
                  {!message.read && !message.from_admin && (
                    <span className="text-xs text-zinc-500 mt-2 block">Tap to mark as read</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Message Input - Fixed Bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4">
        <div className="max-w-md mx-auto flex space-x-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Send message to admin..."
            rows={2}
            className="flex-1 bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
            data-testid="new-message-input"
          />
          <Button
            onClick={sendMessage}
            className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-md px-4"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminInboxPage;